import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios, { getApiUrl } from "@/lib/api";
import { Trophy, Users, BarChart3, Database, Target, Gamepad2, ChevronRight, Zap, Link2, Sparkles, LayoutDashboard, Award, Star, Bell, Download, BookOpen, Settings, LogOut, Image, Swords, Gift, LinkIcon, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/components/ProtectedRoute";

// Platform logos
const PLATFORM_LOGOS = {
  xbox: {
    name: "Xbox",
    logo: "/platform-logos/xbox-green.png",
    color: "#107C10"
  },
  playstation: {
    name: "PlayStation",
    logo: "/platform-logos/playstation.png",
    color: "#003087"
  },
  steam: {
    name: "Steam",
    logo: "/platform-logos/steam.ico",
    color: "#66C0F4"
  }
};

const FEATURES = [
  {
    icon: Gamepad2,
    title: "Multi-Platform Tracking",
    description: "Sync your achievements across Xbox, PlayStation, and Steam. Plus, manually add games from any other platform.",
    color: "#7C3AED",
    span: "md:col-span-2"
  },
  {
    icon: Trophy,
    title: "Achievement Hunting",
    description: "Track completion stats, find rare achievements, and organize your path to 100%.",
    color: "#F59E0B",
    span: ""
  },
  {
    icon: BarChart3,
    title: "Deep Analytics",
    description: "Visualize your gaming habits with detailed statistics and progress tracking.",
    color: "#10B981",
    span: ""
  },
  {
    icon: Users,
    title: "Share",
    description: "Share custom checklists, discover organizational filters, and take on challenges designed for hardcore completionists.",
    color: "#3B82F6",
    span: "md:col-span-2"
  },
  {
    icon: Link2,
    title: "Notes & Quick Links",
    description: "Stop cluttering your browser bookmarks or losing gaming notes in random files. Save everything here and access it from anywhere!",
    color: "#06B6D4",
    span: ""
  },
  {
    icon: Sparkles,
    title: "Custom Games & Achievements",
    description: "Add your own games and achievements. Gamify real-life tasks, or create custom challenges to add replayability to your favorite games!",
    color: "#EC4899",
    span: ""
  }
];

const HOME_TILES = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", desc: "Overview & recent activity", color: "#7C3AED", bg: "from-violet-600/20 to-purple-800/10" },
  { to: "/browse", icon: BookOpen, label: "Browse", desc: "Explore supported games", color: "#06B6D4", bg: "from-cyan-600/20 to-teal-800/10" },
  { to: "/games", icon: Gamepad2, label: "Games", desc: "Your game library", color: "#3B82F6", bg: "from-blue-600/20 to-indigo-800/10" },
  { to: "/wishlist", icon: Gift, label: "Wish List", desc: "Games you want to play", color: "#FBBF24", bg: "from-amber-500/20 to-orange-800/10" },
  { to: "/priorities", icon: Star, label: "Priorities", desc: "Focus & planning", color: "#EF4444", bg: "from-red-500/20 to-rose-800/10" },
  { to: "/multiplayer", icon: Swords, label: "Multiplayer", desc: "Find players & coordinate", color: "#22D3EE", bg: "from-cyan-500/20 to-sky-800/10" },
  { to: "/achievements", icon: Award, label: "Achievements", desc: "Browse all achievements", color: "#F59E0B", bg: "from-amber-600/20 to-yellow-800/10" },
  { to: "/statistics", icon: BarChart3, label: "Statistics", desc: "Progress & analytics", color: "#10B981", bg: "from-emerald-600/20 to-green-800/10" },
  { to: "/signatures", icon: Image, label: "Signatures", desc: "Dynamic stat images", color: "#D946EF", bg: "from-fuchsia-600/20 to-pink-800/10" },
  // Temporarily hidden per request:
  // { to: "/notifications", icon: Bell, label: "Notifications", desc: "Alerts & reminders", color: "#F97316", bg: "from-orange-600/20 to-amber-800/10" },
  { to: "/social", icon: Users, label: "Community Items", desc: "Explore user-created content", color: "#8B5CF6", bg: "from-purple-600/20 to-violet-800/10" },
  { to: "/links", icon: LinkIcon, label: "Links", desc: "Saved links & notes", color: "#0EA5E9", bg: "from-sky-600/20 to-blue-800/10" },
  // { to: "/import-export", icon: Download, label: "Import / Export", desc: "Manage your data", color: "#64748B", bg: "from-slate-600/20 to-gray-800/10", adminOnly: true },
  { to: "/settings", icon: Settings, label: "Settings", desc: "Preferences & config", color: "#78716C", bg: "from-stone-600/20 to-neutral-800/10" },
  { to: "/tutorials", icon: GraduationCap, label: "Tutorials & FAQs", desc: "Guides & help", color: "#14B8A6", bg: "from-teal-600/20 to-emerald-800/10" },
];

function LoggedInHome({ user, logout }) {
  const navigate = useNavigate();
  const firstName = user?.name || "Gamer";

  // New-items counts for Browse (new games), Community Items, and Tutorials —
  // mirrors the unread badges in the sidebar.
  const [newCounts, setNewCounts] = useState({ "/browse": 0, "/social": 0, "/tutorials": 0, "/multiplayer": 0 });
  useEffect(() => {
    const API = `${getApiUrl()}/api`;
    let active = true;
    (async () => {
      const [b, c, t, m] = await Promise.all([
        axios.get(`${API}/master/browse/notifications/summary`).catch(() => ({ data: { total: 0 } })),
        axios.get(`${API}/community/notifications/summary`).catch(() => ({ data: { total: 0 } })),
        axios.get(`${API}/tutorials/notifications/summary`).catch(() => ({ data: { total: 0 } })),
        axios.get(`${API}/multiplayer/notifications/summary`).catch(() => ({ data: { total: 0 } })),
      ]);
      if (active) {
        setNewCounts({
          "/browse": b.data?.total || 0,
          "/social": c.data?.total || 0,
          "/tutorials": t.data?.total || 0,
          "/multiplayer": m.data?.total || 0,
        });
      }
    })();
    return () => { active = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Top bar */}
      <nav className="sticky top-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 md:gap-3" data-testid="home-logo">
            <img src="/logo.png" alt="Game-o-Rang" className="w-8 h-8 md:w-10 md:h-10 rounded-lg" />
            <span className="font-heading font-bold text-lg md:text-xl tracking-tight hidden sm:block">Game-o-Rang</span>
          </Link>
          <div className="flex items-center gap-3">
            {user?.picture && (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-white/10" />
            )}
            <span className="text-sm text-white/60 hidden sm:block">{user?.email}</span>
            <Button
              variant="ghost"
              size="sm"
              className="text-white/40 hover:text-white"
              onClick={() => { logout(); navigate("/"); }}
              data-testid="home-logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Greeting */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-12 md:pt-16 pb-4">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight" data-testid="home-greeting">
          Welcome back, <span className="bg-gradient-to-r from-[#7C3AED] to-[#A78BFA] bg-clip-text text-transparent">{firstName}</span>
        </h1>
        <p className="text-white/50 mt-2 text-base md:text-lg">Where do you want to go?</p>
      </div>

      {/* Tile grid */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
          {HOME_TILES.filter((tile) => !tile.adminOnly || user?.isAdmin).map((tile) => (
            <Link
              key={tile.to}
              to={tile.to}
              className="group relative overflow-hidden rounded-2xl border border-white/[0.06] hover:border-white/15 bg-white/[0.02] hover:bg-white/[0.04] transition-all duration-300 hover:scale-[1.03] hover:shadow-lg"
              data-testid={`home-tile-${tile.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
            >
              {/* Background gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${tile.bg} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

              {/* New-items count badge */}
              {newCounts[tile.to] > 0 && (
                <span
                  className="absolute top-2 right-2 z-20 min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold shadow-md"
                  data-testid={`home-tile-badge-${tile.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                >
                  {newCounts[tile.to] > 99 ? "99+" : newCounts[tile.to]}
                </span>
              )}

              <div className="relative z-10 p-5 md:p-6 flex flex-col items-center text-center gap-3">
                <div
                  className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                  style={{ backgroundColor: `${tile.color}15` }}
                >
                  <tile.icon className="w-7 h-7 md:w-8 md:h-8" style={{ color: tile.color }} />
                </div>
                <div>
                  <p className="font-heading font-bold text-sm md:text-base text-white group-hover:text-white transition-colors">{tile.label}</p>
                  <p className="text-[11px] md:text-xs text-white/35 mt-0.5 leading-snug">{tile.desc}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-auto py-8 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto text-center">
          <span className="text-white/30 text-xs">Game-o-Rang v1.0.0</span>
        </div>
      </footer>
    </div>
  );
}

export default function LandingPage() {
  const { user, isAuthenticated, isLoading, logout } = useAuth();

  // If authenticated, show the home page
  if (!isLoading && isAuthenticated) {
    return <LoggedInHome user={user} logout={logout} />;
  }

  // If still loading auth, show a brief loading state rather than flashing the splash
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#7C3AED]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 md:gap-3" data-testid="landing-logo">
            <img 
              src="/logo.png" 
              alt="Game-o-Rang" 
              className="w-8 h-8 md:w-10 md:h-10 rounded-lg"
            />
            <span className="font-heading font-bold text-lg md:text-xl tracking-tight hidden sm:block">Game-o-Rang</span>
          </Link>
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/features" data-testid="nav-features" className="hidden sm:block">
              <Button variant="ghost" className="text-white/70 hover:text-white">
                Features
              </Button>
            </Link>
            <Link to="/login" data-testid="nav-signin">
              <Button variant="ghost" className="text-white/70 hover:text-white text-sm md:text-base px-2 md:px-4">
                Sign In
              </Button>
            </Link>
            <Link to="/login?signup=true" data-testid="nav-signup">
              <Button className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold px-4 md:px-6 text-sm md:text-base">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#7C3AED]/10 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#7C3AED]/5 via-transparent to-transparent" />
        
        {/* Grid pattern overlay */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-6 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6 md:mb-8">
            <Zap className="w-4 h-4 text-[#7C3AED]" />
            <span className="text-xs md:text-sm text-white/70">Built for note-taking gamers</span>
          </div>

          {/* Main headline */}
          <h1 className="font-heading font-black text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl tracking-tighter uppercase leading-none mb-4 md:mb-6">
            <span className="bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent block">
              Organize Your Game.
            </span>
            <span className="bg-gradient-to-r from-[#7C3AED] to-[#A78BFA] bg-clip-text text-transparent block">
              Own Every Detail.
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-base md:text-lg lg:text-xl text-white/60 max-w-2xl mx-auto mb-8 md:mb-10 leading-relaxed px-2">
            Your personal gaming notebook. Take notes on every quest, mark collectibles on custom maps, plan your runs, and keep your whole library — Xbox, PlayStation, Steam, or any platform — in one place. Cheat guides and external sites optional.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 mb-12 md:mb-16">
            <Link to="/login?signup=true" data-testid="hero-signup" className="w-full sm:w-auto">
              <Button 
                size="lg" 
                className="w-full sm:w-auto bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-heading font-bold uppercase tracking-wider px-6 md:px-8 py-5 md:py-6 text-base md:text-lg shadow-[0_0_30px_rgba(124,58,237,0.3)] hover:shadow-[0_0_50px_rgba(124,58,237,0.5)] transition-all hover:scale-105"
              >
                Get Started Free
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/features" data-testid="hero-features" className="w-full sm:w-auto">
              <Button 
                size="lg" 
                variant="outline" 
                className="w-full sm:w-auto border-white/20 hover:border-white/40 text-white font-heading uppercase tracking-wider px-6 md:px-8 py-5 md:py-6 text-base md:text-lg hover:bg-white/5"
              >
                Learn More
              </Button>
            </Link>
          </div>

          {/* Platform logos */}
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {Object.entries(PLATFORM_LOGOS).map(([key, platform]) => (
              <div 
                key={key} 
                className="group flex items-center gap-3 opacity-50 hover:opacity-100 transition-all duration-300"
                title={platform.name}
              >
                <img 
                  src={platform.logo} 
                  alt={platform.name} 
                  className="w-8 h-8 md:w-10 md:h-10 object-contain grayscale group-hover:grayscale-0 transition-all"
                />
                <span className="hidden md:block text-sm text-white/50 group-hover:text-white/80 transition-colors">
                  {platform.name}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/20 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/40 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Features Bento Grid */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section header */}
          <div className="text-center mb-16">
            <span className="text-sm font-mono text-[#7C3AED] uppercase tracking-widest mb-4 block">
              Features
            </span>
            <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mb-4">
              Everything You Need
            </h2>
            <p className="text-white/60 text-lg max-w-xl mx-auto">
              Powerful tools designed for serious achievement hunters and casual gamers alike.
            </p>
          </div>

          {/* Bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {FEATURES.map((feature, index) => (
              <div
                key={index}
                className={`group relative overflow-hidden bg-white/[0.02] border border-white/10 hover:border-[#7C3AED]/50 rounded-2xl p-8 transition-all duration-500 hover:bg-white/[0.04] ${feature.span}`}
              >
                {/* Gradient glow on hover */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: `radial-gradient(circle at 50% 0%, ${feature.color}15, transparent 60%)`
                  }}
                />
                
                <div className="relative z-10">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-6"
                    style={{ backgroundColor: `${feature.color}20` }}
                  >
                    <feature.icon className="w-6 h-6" style={{ color: feature.color }} />
                  </div>
                  <h3 className="font-heading font-bold text-xl mb-3">{feature.title}</h3>
                  <p className="text-white/60 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* View all features link */}
          <div className="text-center mt-12">
            <Link to="/features" data-testid="view-all-features">
              <Button variant="ghost" className="text-[#7C3AED] hover:text-[#A78BFA] font-semibold">
                View All Features
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mb-6">
            Ready to Level Up?
          </h2>
          <p className="text-white/60 text-lg mb-10 max-w-xl mx-auto">
            Start organizing your gaming achievements and never lose track of your progress again.
          </p>
          <Link to="/login?signup=true" data-testid="final-cta">
            <Button 
              size="lg" 
              className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-heading font-bold uppercase tracking-wider px-10 py-6 text-lg shadow-[0_0_30px_rgba(124,58,237,0.3)] hover:shadow-[0_0_50px_rgba(124,58,237,0.5)] transition-all hover:scale-105"
            >
              Start Tracking Free
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img 
              src="/logo.png" 
              alt="Game-o-Rang" 
              className="w-8 h-8 rounded-lg"
            />
            <span className="text-white/50 text-sm">
              © 2025 Game-o-Rang. All rights reserved.
            </span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/features" className="text-white/50 hover:text-white text-sm transition-colors">
              Features
            </Link>
            <Link to="/login" className="text-white/50 hover:text-white text-sm transition-colors">
              Sign In
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
